import { Link } from "react-router-dom";
import { SCORE_BY_SLUG } from "@/data/scores";
import { STAGES } from "./curriculum";

/**
 * The course at a glance: eight stages, and which pieces are ready.
 *
 * Two things this has to communicate that a plain list cannot:
 *  - the SPIRAL. Twinkle appears three times and Jesu Joy twice, each time with
 *    more texture. Those repeats are shown grouped so the student can see they
 *    are re-reading music they already know, not starting again.
 *  - what is BUILT. Stages describe the finished curriculum, including pieces
 *    still being engraved, so a stage can show its aim before its music exists.
 */
export function StageMap({
  accentHex, current,
}: {
  accentHex: string;
  current?: string;
}) {
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-zinc-100">The eight stages</h2>
      <ol className="space-y-3">
        {STAGES.map((stage) => {
          const built = stage.pieces.filter((p) => SCORE_BY_SLUG[p]);
          const pending = stage.pieces.length - built.length;
          const active = stage.pieces.includes(current ?? "");
          return (
            <li
              key={stage.n}
              className={`rounded-2xl border p-4 transition ${
                active ? "bg-zinc-900/80" : "border-zinc-800 bg-zinc-900/40"
              }`}
              style={active ? { borderColor: accentHex } : undefined}
            >
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span
                  className="font-mono text-xs tabular-nums"
                  style={{ color: accentHex }}
                >
                  {String(stage.n).padStart(2, "0")}
                </span>
                <h3 className="text-base font-semibold text-zinc-100">
                  {stage.title}
                </h3>
                <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                  {built.length}/{stage.pieces.length} ready
                </span>
              </div>

              <p className="mt-1 text-sm leading-relaxed text-zinc-400">
                {stage.aim}
              </p>

              {stage.n === 3 && (
                <p className="mt-2 rounded-lg border-l-2 border-zinc-600 bg-zinc-800/50 p-2.5 text-xs leading-relaxed text-zinc-400">
                  New for this edition. The printed book introduced the bass clef
                  already inside a two-hand grand staff, which is where most
                  beginners stall — this stage teaches it on its own first.
                </p>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                {stage.pieces.map((slug) => {
                  const score = SCORE_BY_SLUG[slug];
                  if (!score) {
                    return (
                      <span
                        key={slug}
                        className="rounded-full border border-dashed border-zinc-700 px-3 py-1.5 text-xs text-zinc-600"
                      >
                        {prettify(slug)} · being engraved
                      </span>
                    );
                  }
                  const isCurrent = slug === current;
                  return (
                    <Link
                      key={slug}
                      to={`/lesson/sight-reading-${score.n}`}
                      title={score.fillsGap}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                        isCurrent
                          ? "text-zinc-950"
                          : "border border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                      }`}
                      style={isCurrent ? { background: accentHex } : undefined}
                    >
                      {score.title}
                      {/* A student should be able to see which music came from
                          Jason's printed book and which was written to fill a
                          gap in it. */}
                      {score.newForThisEdition && (
                        <span className="ml-1.5 opacity-60">new</span>
                      )}
                    </Link>
                  );
                })}
              </div>

              {pending > 0 && stage.n !== 3 && (
                <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-zinc-600">
                  {pending} still being engraved
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** A readable label for a piece that has no score file yet. */
function prettify(slug: string): string {
  return slug.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}
